<template>
  <CModal
    :show.sync="visible"
    size="lg"
    centered
    @hidden="onHidden"
  >
    <template #header>
      <div>
        <div class="d-flex align-items-center">
          <CIcon name="cipPencil" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title">
              <translate translate-context="collaborators.edit">Edit account email</translate>
            </h5>
          </div>
        </div>
      </div>
      <CButtonClose @click="hide"/>
    </template>
    <div>
      <div class="form-group">
        <label>
          <translate translate-context="collaborators.edit">Current Email</translate>
        </label>
        <CInput
          v-model="currentEmail"
          type="email"
          disabled
          readonly
          class="bg-light"
        />
      </div>
      
      <div class="form-group">
        <label for="new-email">
          <translate translate-context="collaborators.edit">New Email Address</translate>
        </label>
        <CInput
          id="new-email"
          v-model="form.email"
          type="email"
          required
          :placeholder="$gettext('Enter new email address')"
          maxlength="254"
        />
        <div v-if="emailError" class="text-danger small mt-2">
          {{ emailError }}
        </div>
      </div>
      
      <div class="form-group">
        <div class="custom-control custom-checkbox">
          <input
            id="resend-invitation"
            v-model="form.resendInvitation"
            type="checkbox"
            class="custom-control-input"
          />
          <label for="resend-invitation" class="custom-control-label">
            {{ $gettext('Resend invitation after updating email') }}
          </label>
        </div>
        <small class="text-muted ml-4">
          <translate translate-context="collaborators.edit">If checked, a new invitation will be sent to the updated email address</translate>
        </small>
      </div>
    </div>
    
    <template #footer>
      <CButton color="secondary" @click="hide">
        <translate translate-context="collaborators.edit">Cancel</translate>
      </CButton>
      <CButton
        color="primary"
        @click="onSubmit"
        :disabled="!isFormValid || isSubmitting"
      >
        <span v-if="isSubmitting">
          <CSpinner size="sm" class="mr-1"/>
          <translate translate-context="collaborators.edit">Updating...</translate>
        </span>
        <span v-else>
          <translate translate-context="collaborators.edit">Update Email</translate>
        </span>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'EditEmailModal',
  
  props: {
    scope: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      visible: false,
      collaborator: null,
      currentEmail: '',
      form: {
        email: '',
        resendInvitation: true
      },
      emailError: '',
      isSubmitting: false
    }
  },
  
  computed: {
    isFormValid() {
      return this.form.email && 
             this.form.email !== this.currentEmail && 
             this.validateEmail(this.form.email) &&
             !this.emailError
    }
  },
  
  methods: {
    ...mapActions('collaborators', [
      'updateCollaboratorEmail',
      'resendInvitation'
    ]),
    
    show(collaborator) {
      this.collaborator = collaborator
      this.currentEmail = collaborator.email
      this.form.email = ''
      this.form.resendInvitation = true
      this.emailError = ''
      this.isSubmitting = false
      this.visible = true
    },
    
    hide() {
      this.visible = false
    },
    
    onHidden() {
      this.collaborator = null
      this.currentEmail = ''
      this.form.email = ''
      this.form.resendInvitation = true
      this.emailError = ''
      this.isSubmitting = false
    },
    
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },
    
    async onSubmit() {
      if (!this.isFormValid || this.isSubmitting) return
      
      this.isSubmitting = true
      this.emailError = ''
      
      try {
        // Update email
        await this.updateCollaboratorEmail({
          collaboratorId: this.collaborator.id,
          email: this.form.email.toLowerCase()
        })
        
        // Resend invitation if requested
        if (this.form.resendInvitation) {
          try {
            await this.resendInvitation(this.collaborator.id)
            await this.notifySuccess(this.$pgettext('collaborators.notification', 'Email updated and invitation resent successfully'))
          } catch (error) {
            // Email updated but resend failed
            await this.notifyMessage({
              text: this.$pgettext('collaborators.notification', 'Email updated but failed to resend invitation'),
              messageType: 'warning'
            })
          }
        } else {
          await this.notifySuccess(this.$pgettext('collaborators.notification', 'Email updated successfully'))
        }
        
        this.$emit('updated')
        this.hide()
      } catch (error) {
        if (error.data?.code === 'duplicate_collaborator_email') {
          this.emailError = this.$pgettext('collaborators.error', 'This email is already used by another collaborator')
        } else if (error.data?.code === 'invalid_collaborator_status') {
          this.emailError = this.$pgettext('collaborators.error', 'Email can only be updated for pending collaborators')
        } else if (error.data?.email) {
          this.emailError = error.data.email[0]
        } else {
          await this.notifyError(this.$pgettext('collaborators.notification', 'Failed to update email'))
        }
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
</style>